import { z } from 'zod';
import type { UserRole } from '../types/database';
import { EVENT_DESCRIPTION_MAX_LENGTH } from '../types/events.types';
import { getEventDescriptionPlainTextFromHtml } from '../utils/event-description.utils';

/**
 * Zod schema for validating event form values
 */
export const eventFormSchema = z
  .object({
    title: z.string().trim().min(3, 'Title is required'),
    description: z.string().superRefine((value, ctx) => {
      const plainText = getEventDescriptionPlainTextFromHtml(value);

      if (!plainText) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Description is required',
        });
      }

      if (plainText.length > EVENT_DESCRIPTION_MAX_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Keep the description under ${EVENT_DESCRIPTION_MAX_LENGTH} characters`,
        });
      }
    }),
    location: z.string().trim().min(2, 'Add a location'),
    eventDate: z.string().min(1, 'Event date is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().optional(),
    eventType: z.string().min(1, 'Event type is required'),
    rsvpDeadline: z.string().optional(),
    capacity: z.preprocess((value) => {
      if (value === '' || value === null || value === undefined) {
        return undefined;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    }, z.number().int().positive().optional()),
    invitationGroups: z.instanceof(Set<UserRole>).optional(),
    eventGroupTargetIds: z.instanceof(Set<string>).optional(),
    eventLink: z
      .union([z.string().trim().url('Please enter a valid URL'), z.literal('')])
      .optional(),
    linkName: z.string().trim().optional(),
    recurrenceType: z.enum(['none', 'weekly', 'biweekly', 'monthly']),
    recurrenceCount: z
      .number()
      .int()
      .min(2, 'Must have at least 2 occurrences')
      .max(52, 'Maximum 52 occurrences allowed'),
  })
  .superRefine((value, ctx) => {
    // Validate end time is after start time
    if (value.eventDate && value.startTime && value.endTime) {
      const start = new Date(`${value.eventDate}T${value.startTime}`).getTime();
      const end = new Date(`${value.eventDate}T${value.endTime}`).getTime();

      if (!Number.isNaN(start) && !Number.isNaN(end) && end <= start) {
        ctx.addIssue({
          path: ['endTime'],
          code: z.ZodIssueCode.custom,
          message: 'End time must be after start time',
        });
      }
    }

    if (
      (!value.invitationGroups || value.invitationGroups.size === 0) &&
      (!value.eventGroupTargetIds || value.eventGroupTargetIds.size === 0)
    ) {
      ctx.addIssue({
        path: ['invitationGroups'],
        code: z.ZodIssueCode.custom,
        message: 'Select at least one audience target',
      });
    }

    // Validate recurrence count is only required if recurrence is enabled
    if (
      value.recurrenceType !== 'none' &&
      (!value.recurrenceCount || value.recurrenceCount < 2)
    ) {
      ctx.addIssue({
        path: ['recurrenceCount'],
        code: z.ZodIssueCode.custom,
        message: 'Must have at least 2 occurrences for recurring events',
      });
    }
  });
